import { Component, Emit, Prop, Ref, Watch } from 'vue-property-decorator'
import GridHeader from '@/components/GridHeader.vue'
import GridDisplayOptions from '@/components/GridDisplayOptions.vue'
import BillingGridBottomPanelActionsComponent from '@/modules/billing-module/components/grid/components/bottom-panel/components/actions/billing-grid-bottom-panel-actions.vue'
import BillingGridBottomPanelActions from '@/modules/billing-module/components/grid/components/bottom-panel/components/actions/billing-grid-bottom-panel-actions'
import VtGrid from '@/components/VtGrid/VtGrid.vue'
import BaseCrudClientSideComponent from '@/shared/resources/base-crud-client-side.component'
import { AppliedBillableConceptResponse } from '@/api/services/billing/applied-billable-concept/billing-applied-billable-concept.dto'
import { BaseCrud } from '@/shared/models/cruds.models'
import { BillingCaseResponse } from '@/api/services/billing/case-grid/billing-case.dto'
import { GetContextMenuItemsParams, MenuItemDef, SelectionChangedEvent } from '@ag-grid-enterprise/all-modules'
import { BillingStoreHelper } from '@/modules/billing-module/store/billing-store.helper'
import {
    BillingGridBottomPanelState,
    initialBillingGridState
} from '@/modules/billing-module/components/grid/components/bottom-panel/billing-grid-bottom-panel.model'
import { CommonFilterModel, CommonParams } from '@/api/resources/common/shared/models/common.models'
import { GridUtils } from '@/shared/util/grid-utils'
import { PAGINATOR_BIG_PAGE_SIZE } from '@/shared/services/services-constants'
import { PromiseUtils } from '@/shared/util/promise-utils'
import { billingAppliedBillableConceptService } from '@/api/services/billing/applied-billable-concept/billing-applied-billable-concept.service'
import { Nullable } from '@/shared/types/app.types'
import { store } from '@/store/store'

@Component({
    components: {
        GridHeader,
        GridDisplayOptions,
        BillingGridBottomPanelActionsComponent,
        VtGrid
    }
})
export default class BillingGridBottomPanel extends BaseCrudClientSideComponent<AppliedBillableConceptResponse> implements BaseCrud {
    @Prop({ type: Object }) readonly caseGrid!: Nullable<BillingCaseResponse>
    @Prop({ type: Function }) readonly contextMenuItemsFn!: Nullable<(params: GetContextMenuItemsParams) => MenuItemDef[]>

    @Ref() readonly actions!: BillingGridBottomPanelActions

    @Emit()
    closeBottomPanel(): void {
        // emits an event
    }

    @Emit()
    refresh(): void {
        // emits an event
    }

    @Watch('caseGrid.id')
    async onCaseIdChanged(): Promise<void> {
        await this._loadRowData()
    }

    state: BillingGridBottomPanelState
    constructor() {
        super()
        this.state = initialBillingGridState()
    }

    get visibleBottomPanel(): boolean {
        return !!this.caseGrid && !!this.state.rowData?.length
    }

    get gridTitle(): string {
        return this.$tc('accounting.detail.caseConcepts', 1, { caseNumber: this.caseGrid?.caseNumber })
    }
    get gridSubtitle(): string {
        if (this.state.selectedRows.length) {
            const choice = this.state.selectedRows.length > 1 ? 2 : 1
            return `${this.state.selectedRows.length} ${this.$tc('global.selectedElements', choice)}`
        }
        return ''
    }

    private async _getAppliedBillableConcepts(): Promise<AppliedBillableConceptResponse[]> {
        if (this.caseGrid) {
            const filterModel: CommonFilterModel = {
                'caseStudy.id': GridUtils.getNumberFilterModel('equals', this.caseGrid.id)
            }
            const params: CommonParams = {
                size: PAGINATOR_BIG_PAGE_SIZE
            }
            const response =
                await PromiseUtils.toCallSecure(
                    billingAppliedBillableConceptService.paginator(filterModel, params, store.state.labId))
            return response?.content ?? []
        }
        return []
    }
    private async _loadRowData(): Promise<void> {
        this.setRowData(await this._getAppliedBillableConcepts())
        this.state.gridApi?.setRowData(this.state.rowData ?? [])
    }

    /// VUE HOOKS ///

    async mounted(): Promise<void> {
        await this._loadRowData()
    }

    beforeDestroy(): void {
        BillingStoreHelper.detailSelectedRows = []
    }

    /// AG GRID HOOKS ///

    onSelectionChanged({ api }: SelectionChangedEvent): void {
        this.state = {
            ...this.state,
            selectedRows: api.getSelectedRows()
        }
        BillingStoreHelper.detailSelectedRows = api.getSelectedRows()
    }

    getContextMenuItems(): MenuItemDef[] {
        return this.actions.getContextMenuItems()
    }

    onClearFilter(): void {
        this.state.gridApi?.setFilterModel(null)
    }

    async reloadGrid(): Promise<void> {
        this.refresh() // emits an event
        await this._loadRowData()
    }
}
